import React, { useState, useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import WelcomeImage from '@/assets/images/onboarding/welcome.webm';
import ActivityCalendar from '@/assets/images/onboarding/activity-calendar.webm';
import Analytics from '@/assets/images/onboarding/analytics.webm';
import { detectDeviceAndBrowser } from '@/utils/deviceAndBrowser';

interface OnboardingManagerProps {
    children: React.ReactNode;
}

const OnboardingManager: React.FC<OnboardingManagerProps> = ({ children }) => {
    const { t } = useTranslation('onboarding');
    const [showModal, setShowModal] = useState<boolean>(true);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // デバイス判定
    const deviceCode = detectDeviceAndBrowser();
    const isMobile =
        deviceCode.includes('IOS_') ||
        deviceCode.includes('ANDROID_') ||
        deviceCode === 'MOBILE_OTHER';

    const welcomePointsRaw = t('steps.welcome.points', { returnObjects: true }) as unknown;
    const welcomePoints = Array.isArray(welcomePointsRaw)
        ? (welcomePointsRaw as string[])
        : typeof welcomePointsRaw === 'string'
        ? [welcomePointsRaw]
        : [];
    const analyticsPointsRaw = t('steps.analytics.points', { returnObjects: true }) as unknown;
    const analyticsPoints = Array.isArray(analyticsPointsRaw)
        ? (analyticsPointsRaw as string[])
        : typeof analyticsPointsRaw === 'string'
        ? [analyticsPointsRaw]
        : [];
    // 動画の再生速度を設定
    useEffect(() => {
        videoRefs.current.forEach((video, index) => {
            if (video) {
                const rates = [1, 2, 1.5]; // 各動画の再生速度
                video.playbackRate = rates[index] || 1;
            }
        });
    }, [currentStep]);

    // モーダル表示時に背景のスクロールを無効化
    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // クリーンアップ
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showModal]);

    // オンボーディングのステップ定義
    const onboardingSteps = [
        {
            title: t('steps.welcome.title'),
            content: (
                <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-center my-2">
                        {t('steps.welcome.heading')}
                    </h2>
                    <video
                        ref={(el) => {
                            videoRefs.current[0] = el;
                        }}
                        src={WelcomeImage}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-auto"
                    />
                    <div className="space-y-6 px-8 leading-6 tracking-widest">
                        <p className="text-center text-gray-600">{t('steps.welcome.intro')}</p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2">
                            {welcomePoints.map((point, index) => (
                                <li key={index} className="pl-2">
                                    {point}
                                </li>
                            ))}
                        </ul>
                        <p className="text-center text-gray-600">{t('steps.welcome.calendar')}</p>
                        <p className="text-center text-gray-600">{t('steps.welcome.cta')}</p>
                    </div>
                </div>
            ),
        },
        {
            title: t('steps.activity.title'),
            content: (
                <div className="space-y-4 px-6 leading-6 tracking-widest">
                    <h2 className="text-2xl font-bold text-center my-2">
                        {t('steps.activity.heading')}
                    </h2>
                    <div className="space-y-3">
                        <p className="text-center text-gray-600">
                            <Trans
                                ns="onboarding"
                                i18nKey="steps.activity.description"
                                components={{ br: <br /> }}
                            />
                        </p>
                        <video
                            ref={(el) => {
                                videoRefs.current[1] = el;
                            }}
                            src={ActivityCalendar}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-auto"
                        />
                        <p className="text-center text-gray-600">
                            {t('steps.activity.example')}
                        </p>
                    </div>
                </div>
            ),
        },
        {
            title: t('steps.analytics.title'),
            content: (
                <div className="space-y-4 px-6 leading-7 tracking-widest">
                    <h2 className="text-2xl font-bold text-center my-2">
                        {t('steps.analytics.heading')}
                    </h2>
                    <div className="space-y-3">
                        <p className="text-center text-gray-600">{t('steps.analytics.intro')}</p>
                        <video
                            ref={(el) => {
                                videoRefs.current[2] = el;
                            }}
                            src={Analytics}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-auto"
                        />
                        <p className="text-center text-gray-600">
                            {t('steps.analytics.details')}
                        </p>
                        <ul className="list-disc list-inside text-gray-600 space-y-2">
                            {analyticsPoints.map((point, index) => (
                                <li key={index} className="pl-2">
                                    {point}
                                </li>
                            ))}
                        </ul>
                        <p className="text-center text-gray-600">{t('steps.analytics.cta')}</p>
                    </div>
                </div>
            ),
        },
        {
            title: t('steps.habit.title'),
            content: (
                <div className="space-y-4 px-6 leading-7 tracking-widest">
                    <h2 className="text-2xl font-bold text-center my-2">
                        {t('steps.habit.heading')}
                    </h2>
                    <div className="space-y-3">
                        <p className="text-center text-gray-600">{t('steps.habit.intro')}</p>
                        <p className="text-center text-gray-600">
                            {t('steps.habit.recommendation')}
                        </p>
                        {/* デバイス判定 */}
                        {isMobile ? (
                            <div className="space-y-2 text-gray-600">
                                <h4 className="font-semibold text-xl">
                                    {t('steps.habit.mobile.bookmarkHeading')}
                                </h4>
                                <p>{t('steps.habit.mobile.bookmarkDescription')}</p>
                                <br />
                                <h4 className="font-semibold text-xl">
                                    {t('steps.habit.mobile.appHeading')}
                                </h4>
                                <h5 className="font-semibold pt-2">
                                    {t('steps.habit.mobile.chromeHeading')}
                                </h5>
                                <p>{t('steps.habit.mobile.chromeDescription')}</p>
                                <h5 className="font-semibold pt-2">
                                    {t('steps.habit.mobile.safariHeading')}
                                </h5>
                                <p>{t('steps.habit.mobile.safariDescription')}</p>
                            </div>
                        ) : (
                            <div className="space-y-2 text-gray-600">
                                <h4 className="font-semibold text-xl">
                                    {t('steps.habit.desktop.bookmarkHeading')}
                                </h4>
                                <p>
                                    <Trans
                                        ns="onboarding"
                                        i18nKey="steps.habit.desktop.bookmarkDescription"
                                        components={{ br: <br /> }}
                                    />
                                </p>
                                <br />
                                <h4 className="font-semibold text-xl">
                                    {t('steps.habit.desktop.appHeading')}
                                </h4>
                                <h5 className="font-semibold pt-2">
                                    {t('steps.habit.desktop.chromeHeading')}
                                </h5>
                                <p>
                                    <Trans
                                        ns="onboarding"
                                        i18nKey="steps.habit.desktop.chromeDescription"
                                        components={{ br: <br /> }}
                                    />
                                </p>

                                <h5 className="font-semibold pt-2">
                                    {t('steps.habit.desktop.edgeHeading')}
                                </h5>
                                <p>{t('steps.habit.desktop.edgeDescription')}</p>
                            </div>
                        )}
                        <p className="text-center text-gray-600 pt-10 font-bold">
                            {t('steps.habit.cta')}
                        </p>
                    </div>
                </div>
            ),
        },
    ];

    // 次のステップへ
    const nextStep = () => {
        if (currentStep < onboardingSteps.length - 1) {
            setCurrentStep(currentStep + 1);
            // スクロール位置をリセット
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
        } else {
            completeOnboarding();
        }
    };

    // 前のステップへ
    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            // スクロール位置をリセット
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
        }
    };

    // オンボーディング完了
    const completeOnboarding = () => {
        setShowModal(false);
        sessionStorage.removeItem('isNewUser');
        sessionStorage.setItem('onboarding_completed', 'true');
        // オンボーディング完了イベントを発火
        window.dispatchEvent(new CustomEvent('onboarding-completed'));
    };

    // モーダルの表示
    const renderModal = () => {
        if (!showModal) return null;

        const currentStepData = onboardingSteps[currentStep];
        if (!currentStepData) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                {/* 背景クリックでは閉じない */}
                <div className="absolute inset-0 bg-black bg-opacity-50" />

                <div className="relative bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col">
                    {/* スクロールエリア */}
                    <div
                        ref={scrollContainerRef}
                        className="flex-1 overflow-y-auto py-6 lg:px-6 relative"
                    >
                        <div className="space-y-4 pb-10">{currentStepData.content}</div>
                    </div>
                    {/* 下部フェードアウト効果 - スクロールエリアの固定位置 */}
                    <div className="absolute bottom-24 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none"></div>

                    {/* ボタンと進捗表示を固定 */}
                    <div className="flex-shrink-0 p-6 pt-4">
                        {/* ボタンを中央寄せ */}
                        <div className="flex justify-center gap-4 mb-4">
                            {currentStep > 0 && (
                                <button
                                    onClick={prevStep}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                >
                                    {t('buttons.back')}
                                </button>
                            )}

                            <button
                                onClick={nextStep}
                                className={`px-4 py-2 rounded hover:opacity-90 ${
                                    currentStep === onboardingSteps.length - 1
                                        ? 'bg-kotomilThemaColor text-black'
                                        : 'outline outline-kotomilThemaColor hover:bg-kotomilThemaColor text-black'
                                }`}
                            >
                                {currentStep === onboardingSteps.length - 1
                                    ? t('buttons.start')
                                    : t('buttons.next')}
                            </button>
                        </div>

                        {/* 進捗表示 - ドットインジケータ */}
                        <div className="flex justify-center gap-2">
                            {onboardingSteps.map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                                        index === currentStep
                                            ? 'bg-kotomilThemaColor'
                                            : 'bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {children}
            {renderModal()}
        </>
    );
};

export default OnboardingManager;
